import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';

const Wrapper = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
  font-weight: 700;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
`;

const CourseCard = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const CourseName = styled.div`
  font-weight: 600;
  color: #111827;
`;

const CourseMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
`;

const StatusRow = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4b5563;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${props => props.$color || '#22c55e'};
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.15);
`;

const ProgressWrapper = styled.div`
  margin-top: 12px;
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${props =>
    props.$completed
      ? '#22c55e'
      : 'linear-gradient(90deg, #22c55e, #eab308, #ef4444)'};
  width: ${props => `${props.$value || 0}%`};
  transition: width 0.25s ease-out;
`;

const ProgressLabel = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 12px;
  background: #f9fafb;
  border: 1px dashed #e5e7eb;
  color: #6b7280;
`;

const NoScheduleCard = styled(EmptyState)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  background: linear-gradient(135deg, #f9fafb, #eff6ff);
`;

const NoScheduleBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: #e0f2fe;
  color: #0369a1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 20px;
`;

const NoScheduleTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #0f172a;
`;

const NoScheduleHint = styled.div`
  font-size: 13px;
  color: #475569;
`;

const NOTICE_TONES = {
  green: { border: '#bbf7d0', bg: '#f0fdf4', iconBg: '#dcfce7', strong: '#166534', soft: '#15803d' },
  blue: { border: '#bfdbfe', bg: '#eff6ff', iconBg: '#dbeafe', strong: '#1d4ed8', soft: '#1e40af' },
  amber: { border: '#fde68a', bg: '#fffbeb', iconBg: '#fef3c7', strong: '#92400e', soft: '#b45309' },
};
const noticeTone = (tone) => NOTICE_TONES[tone] || NOTICE_TONES.amber;

const NOTICE_PRESETS = {
  graduation_eligible: {
    tone: 'green',
    icon: '🎓',
    title: 'Important notice: Eligible to graduate',
    text: () =>
      "Congratulations! You've completed all submissions across every semester and are eligible to graduate.",
  },
  on_track: {
    tone: 'blue',
    icon: '✓',
    title: 'Important notice: Semester progress is on track',
    text: (e) =>
      `Your batch's current semester is Semester ${e.batchCurrentSemester}, and you are eligible for Semester ${e.eligibleSemester}. Keep up your submissions to stay on track.`,
  },
  ahead: {
    tone: 'green',
    icon: '✓',
    title: 'Important notice: You are ahead of schedule',
    text: (e) =>
      `Your batch's current semester is Semester ${e.batchCurrentSemester}, but you are already eligible for Semester ${e.eligibleSemester}.`,
  },
  behind: {
    tone: 'amber',
    icon: '!',
    title: 'Important notice: Submissions needed to advance',
    text: (e) =>
      `Your batch's current semester is Semester ${e.batchCurrentSemester}. Based on your submission history, you are eligible for Semester ${e.eligibleSemester}. Complete your pending submissions to move up to your batch's current semester.`,
  },
};

const NoticeCard = styled.div`
  margin-top: 20px;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid ${(p) => noticeTone(p.$tone).border};
  background: ${(p) => noticeTone(p.$tone).bg};
  display: flex;
  gap: 12px;
`;

const NoticeIcon = styled.div`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  font-size: 18px;
  background: ${(p) => noticeTone(p.$tone).iconBg};
  color: ${(p) => noticeTone(p.$tone).strong};
`;

const NoticeBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoticeTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: ${(p) => noticeTone(p.$tone).strong};
`;

const NoticeText = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => noticeTone(p.$tone).soft};
`;

const NoticeStatsRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const NoticeStat = styled.div`
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid ${(p) => noticeTone(p.$tone).border};
  color: ${(p) => noticeTone(p.$tone).strong};
`;

const Section = styled.section`
  margin-top: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px;
  font-weight: 600;
  color: #111827;
`;

const SectionSubtitle = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: #6b7280;
`;

const ScheduleCard = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  margin-top: 8px;
`;

const ScheduleRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #374151;
`;

const ScheduleMeta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
`;

const computeProgress = (reg) => {
  if (!reg) return 0;

  const status = String(reg.batchStatus || '').toLowerCase();
  if (status === 'delivery completed' || status === 'batch completed') {
    return 100;
  }

  const startStr = reg.batchStartingDate;
  const endStr = reg.batchEndingDate;

  if (!startStr || !endStr) return 0;

  const start = new Date(startStr);
  const end = new Date(endStr);
  const now = new Date();

  if (!(start instanceof Date && !isNaN(start)) || !(end instanceof Date && !isNaN(end))) {
    return 0;
  }

  if (now <= start) return 0;
  if (now >= end) return 100;

  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;

  const elapsed = now.getTime() - start.getTime();
  return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
};

const getStatusMeta = (reg) => {
  if (!reg) {
    return {
      color: '#9ca3af',
      label: 'No active registration',
      detail: '',
    };
  }

  const {
    registrationStatus,
    deliveryStatus,
    universityID,
    universityRegisterDate,
  } = reg;

  const isCompleted = !!deliveryStatus && [
    'academic delivery completed',
    'graduated',
    'certificate received',
    'certificate dispatched',
  ].includes(String(deliveryStatus).toLowerCase());

  const isUniversityRegistered = !!(universityID || universityRegisterDate);

  let color = '#22c55e';
  let label = 'Active registration';

  if (isCompleted) {
    color = '#6b7280';
    label = 'Delivery completed';
  } else if (!isUniversityRegistered) {
    color = '#f97316';
    label = 'Active · Uni registration pending';
  } else {
    color = '#22c55e';
    label = 'Active · University registered';
  }

  const universityBits = [];
  if (universityID) universityBits.push(`Uni ID: ${universityID}`);
  if (universityRegisterDate) universityBits.push(`Uni reg: ${universityRegisterDate}`);

  const deliveryLabel = deliveryStatus ? `Delivery: ${deliveryStatus}` : null;

  return {
    color,
    label,
    detail: [universityBits.join(' · '), deliveryLabel].filter(Boolean).join(' · '),
  };
};

export default function Dashboard() {
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const noticePreset = eligibility ? NOTICE_PRESETS[eligibility.status] || NOTICE_PRESETS.behind : null;

  useEffect(() => {
    try {
      const data = AuthService.getUser();
      setRegistration(data?.selectedRegistration || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!registration) {
      setEligibility(null);
      return;
    }

    let cancelled = false;
    axiosInstance
      .get('/students/me/semester-eligibility', {
        params: registration.registrationId ? { registrationId: registration.registrationId } : {},
      })
      .then((resp) => {
        if (!cancelled) setEligibility(resp?.data?.applicable ? resp.data : null);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to fetch semester eligibility', err);
      });

    return () => {
      cancelled = true;
    };
  }, [registration]);

  useEffect(() => {
    const batchId = registration?.batchId;
    if (!registration || !batchId) {
      setTodaySchedules([]);
      setScheduleError(null);
      return;
    }

    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);

    setScheduleLoading(true);
    setScheduleError(null);

    axiosInstance
      .get('/students/me/schedules', { params: { date: today, batchId } })
      .then((resp) => {
        if (cancelled) return;
        const items = Array.isArray(resp?.data?.schedules) ? resp.data.schedules : [];
        setTodaySchedules(items);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch today schedules', err);
        const message = err?.response?.data?.message || "Unable to load today's schedule right now.";
        setScheduleError(message);
      })
      .finally(() => {
        if (!cancelled) setScheduleLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [registration]);

  const meta = getStatusMeta(registration);
  const progress = computeProgress(registration);

  const title = registration?.courseName || registration?.courseShortName || 'Dashboard';

  return (
    <StudentLayout title={title}>
      <Wrapper>
        <Heading>Current course & batch</Heading>
        <Subtitle>
          This view shows details of your selected course and batch, including the
          estimated completion progress.
        </Subtitle>

        {loading && <Loader message="Loading your selection…" />}

        {!loading && !registration && (
          <EmptyState>
            We couldn't find a selected course for your account. Please choose one from the
            course selection page.
          </EmptyState>
        )}

        {!loading && registration && (
          <>
            <CourseCard>
              <CourseName>{registration.courseName || registration.courseShortName}</CourseName>
              <CourseMeta>
                {registration.courseShortName && <>{registration.courseShortName} · </>}
                Reg No: {registration.registrationNumber}
                {registration.batchName && <> · Batch: {registration.batchName}</>}
              </CourseMeta>

              <StatusRow>
                <StatusDot $color={meta.color} />
                <span>{meta.label}</span>
                {meta.detail && (
                  <span style={{ color: '#9ca3af' }}>· {meta.detail}</span>
                )}
              </StatusRow>

              <ProgressWrapper>
                <ProgressTrack>
                  <ProgressFill
                    $value={progress}
                    $completed={
                      String(registration.batchStatus || '').toLowerCase() === 'delivery completed' ||
                      String(registration.batchStatus || '').toLowerCase() === 'batch completed'
                    }
                  />
                </ProgressTrack>
                <ProgressLabel>
                  <span>
                    Batch progress: {progress}%
                    {registration.batchStatus ? ` · ${registration.batchStatus}` : ''}
                  </span>
                  <span>
                    {registration.whatsappGroupName
                      ? `WhatsApp: ${registration.whatsappGroupName}`
                      : 'WhatsApp group not set'}
                  </span>
                </ProgressLabel>
              </ProgressWrapper>
            </CourseCard>

            {eligibility && noticePreset && (
              <NoticeCard $tone={noticePreset.tone}>
                <NoticeIcon $tone={noticePreset.tone}>{noticePreset.icon}</NoticeIcon>
                <NoticeBody>
                  <NoticeTitle $tone={noticePreset.tone}>{noticePreset.title}</NoticeTitle>
                  <NoticeText $tone={noticePreset.tone}>{noticePreset.text(eligibility)}</NoticeText>
                  <NoticeStatsRow>
                    <NoticeStat $tone={noticePreset.tone}>
                      Batch semester: {eligibility.batchCurrentSemester}
                    </NoticeStat>
                    <NoticeStat $tone={noticePreset.tone}>
                      {eligibility.status === 'graduation_eligible'
                        ? 'All semesters completed'
                        : `Your eligible semester: ${eligibility.eligibleSemester}`}
                    </NoticeStat>
                    {eligibility.blockingSemester && (
                      <NoticeStat $tone={noticePreset.tone}>
                        Semester {eligibility.blockingSemester.semester}: {eligibility.blockingSemester.completed}/
                        {eligibility.blockingSemester.total} submissions done
                      </NoticeStat>
                    )}
                  </NoticeStatsRow>
                </NoticeBody>
              </NoticeCard>
            )}

            <Section>
              <SectionTitle>Today&apos;s schedule</SectionTitle>
              <SectionSubtitle>
                We&apos;ll show any classes scheduled for your batch today.
              </SectionSubtitle>

              {scheduleLoading && <Loader message="Checking today&apos;s schedule…" />}

              {!scheduleLoading && scheduleError && (
                <EmptyState>{scheduleError}</EmptyState>
              )}

              {!scheduleLoading && !scheduleError && todaySchedules.length === 0 && (
                <NoScheduleCard>
                  <NoScheduleBadge>Free</NoScheduleBadge>
                  <NoScheduleTitle>No classes scheduled today</NoScheduleTitle>
                  <NoScheduleHint>
                    Enjoy a breather or review past materials. New sessions will appear here when scheduled.
                  </NoScheduleHint>
                </NoScheduleCard>
              )}

              {!scheduleLoading && !scheduleError && todaySchedules.length > 0 && (
                todaySchedules.map((s) => (
                  <ScheduleCard key={s.scheduleId}>
                    <ScheduleRow>
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {s.subjectFullName || s.subjectShortName || 'Class'}
                        </div>
                        <ScheduleMeta>
                          {s.batchGroupName && <>Group: {s.batchGroupName} · </>}
                          {s.classroomName ? `Room: ${s.classroomName}` : 'Room not set'}
                        </ScheduleMeta>
                      </div>
                      <div style={{ textAlign: 'right', fontSize: 12 }}>
                        <div>
                          {s.startTime?.slice(0, 5)} - {s.endTime?.slice(0, 5)}
                        </div>
                        {s.staffName && (
                          <div style={{ color: '#6b7280', marginTop: 4 }}>
                            {s.staffName}
                          </div>
                        )}
                      </div>
                    </ScheduleRow>
                  </ScheduleCard>
                ))
              )}
            </Section>
          </>
        )}
      </Wrapper>
    </StudentLayout>
  );
}
